import { FAMILY_LABEL } from '../data/families.js';
import { formatGw, roundGw } from './format.js';

// Hover/focus breakdown of a firm capacity figure. The figure already
// moves as plants are picked, but nothing said why: the variable
// diversity bonus lands inside every variable plant's credit, so a
// player who swaps Offshore Wind for Tidal sees the total jump and
// reads it as Tidal being the better plant, not as a second family
// paying out.
//
// Shared by the build screen's top bar and the Your Grid panel, which
// show the same number and so must explain it the same way.
export function FirmCapacityExplainer({ projection, requiredFirm }) {
  if (!projection) return null;
  const {
    fromPlants = 0,
    fromDiversity = 0,
    fromStorage = 0,
    hasStorage = false,
    variableFamilies = [],
    variableFamiliesAvailable = 0
  } = projection;

  // The total must match the headline figure beside the bar, so it is
  // rounded from the true sum and the diversity row takes up whatever
  // rounding is left over — it is the derived quantity of the three,
  // and the alternative is a column that visibly does not add up.
  const total = roundGw(fromPlants + fromDiversity + fromStorage);
  const plantsRow = roundGw(fromPlants);
  const storageRow = roundGw(fromStorage);
  const diversityRow = roundGw(total - plantsRow - storageRow);

  const owned = variableFamilies.length;
  const familyNames = variableFamilies
    .map((family) => FAMILY_LABEL[family] ?? family)
    .sort()
    .join(', ');

  return (
    <span className="build-firm-info" role="tooltip">
      <span className="build-firm-info-lead">
        What your grid can be counted on to deliver in the single
        busiest hour of the year.
      </span>
      <span className="build-firm-info-rows">
        <span className="build-firm-info-row">
          <span>From plants</span>
          <span>{formatGw(plantsRow)}</span>
        </span>
        <span className="build-firm-info-row">
          <span>Variable mix bonus</span>
          <span>
            {diversityRow > 0 ? '+' : ''}
            {formatGw(diversityRow)}
          </span>
        </span>
        {hasStorage ? (
          <span className="build-firm-info-row">
            <span>Storage at peak</span>
            <span>
              {storageRow > 0 ? '+' : ''}
              {formatGw(storageRow)}
            </span>
          </span>
        ) : null}
        <span className="build-firm-info-row build-firm-info-row-total">
          <span>Total</span>
          <span>{formatGw(total)}</span>
        </span>
        <span className="build-firm-info-row build-firm-info-row-needed">
          <span>Needed</span>
          <span>{formatGw(requiredFirm)}</span>
        </span>
      </span>
      <span className="build-firm-info-note">
        {owned === 0 ? (
          <>
            Variable plants come in {variableFamiliesAvailable} families.
            Owning more than one raises the credit of every variable
            plant you hold.
          </>
        ) : owned >= variableFamiliesAvailable ? (
          <>
            Variable mix: all {variableFamiliesAvailable} families
            ({familyNames}). This bonus is at its cap.
          </>
        ) : owned === 1 ? (
          <>
            Variable mix: 1 of {variableFamiliesAvailable} families
            ({familyNames}). A plant from a <em>different</em> variable
            family would raise the credit of every variable plant you
            own — two wind farms count as one family.
          </>
        ) : (
          <>
            Variable mix: {owned} of {variableFamiliesAvailable} families
            ({familyNames}). Each family beyond the first raises the
            credit of every variable plant you own.
          </>
        )}
      </span>
    </span>
  );
}
